import { useEffect, useState } from "react";

/**
 * useState backed by localStorage so a value survives reloads.
 * Reads once on mount and writes on every change. Safe for SSR (falls back to
 * the initial value when `window` is unavailable) and tolerant of corrupt JSON.
 */
export function usePersistentState<T>(
  key: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage may be full or unavailable; persistence is best-effort */
    }
  }, [key, value]);

  return [value, setValue];
}
